import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Plus, Pencil, RefreshCw, X, Search } from "lucide-react";
import { useApp, can, usePerm } from "@/context/AppContext";
import { pvStatuses, pvStatusCreate, pvStatusUpdate, pvStatusSetActive } from "@/lib/api";

const CATEGORIES = [
  { value: "active_pending",     label: "Active Pending" },
  { value: "ready_for_delivery", label: "Ready for Delivery" },
  { value: "delivered",          label: "Delivered" },
  { value: "cancelled",          label: "Cancelled" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const CAT_TONE = {
  active_pending:     "bg-blue-50 text-blue-700 border-blue-200",
  ready_for_delivery: "bg-amber-50 text-amber-700 border-amber-200",
  delivered:          "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled:          "bg-rose-50 text-rose-700 border-rose-200",
};

// Fresh form seed — used for both Add and Edit forms.
const emptyForm = () => ({
  id: null,
  status_name: "",
  status_category: "active_pending",
  display_order: 10,
  is_active: true,
  edd_mandatory: false,
  remarks_mandatory: false,
  move_to_ready_for_delivery: false,
  close_record: false,
});

const PvStatusMaster = () => {
  const { currentUser } = useApp();
  const canAddMod = usePerm("masters", "add");
  const canEditMod = usePerm("masters", "edit");
  const canWriteRole = can(currentUser, "pv_status_write");
  const canAdd = canWriteRole && canAddMod;
  const canEdit = canWriteRole && canEditMod;

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await pvStatuses(true);
      setRows(d.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load statuses");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLowerCase();
    return rows.filter(
      (r) => r.status_name.toLowerCase().includes(s)
          || CAT_LABEL[r.status_category].toLowerCase().includes(s)
    );
  }, [rows, q]);

  const openAdd = () => {
    const nextOrder = (rows.reduce((m, r) => Math.max(m, r.display_order || 0), 0) || 0) + 10;
    setForm({ ...emptyForm(), display_order: nextOrder });
    setShowForm(true);
  };
  const openEdit = (row) => {
    setForm({ ...row });
    setShowForm(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onCategoryChange = (cat) => {
    // Sensible defaults per category — user can still tweak before saving.
    setForm((f) => {
      const next = { ...f, status_category: cat };
      if (cat === "delivered")          next.close_record = true;
      if (cat === "cancelled")          { next.close_record = true; next.remarks_mandatory = true; }
      if (cat === "ready_for_delivery") next.move_to_ready_for_delivery = true;
      return next;
    });
  };

  const submit = async () => {
    if (!form.status_name.trim())         return toast.error("Status Name is required.");
    if (!form.status_category)            return toast.error("Status Category is required.");
    if (!form.display_order || Number(form.display_order) <= 0)
      return toast.error("Display Order must be a positive number.");
    setSaving(true);
    try {
      const payload = {
        status_name: form.status_name.trim(),
        status_category: form.status_category,
        display_order: Number(form.display_order),
        is_active: !!form.is_active,
        edd_mandatory: !!form.edd_mandatory,
        remarks_mandatory: !!form.remarks_mandatory,
        move_to_ready_for_delivery: !!form.move_to_ready_for_delivery,
        close_record: !!form.close_record,
      };
      if (form.id) {
        await pvStatusUpdate(form.id, payload);
        toast.success("Status updated");
      } else {
        await pvStatusCreate(payload);
        toast.success("Status added");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await pvStatusSetActive(row.id, !row.is_active);
      toast.success(`${row.status_name} ${!row.is_active ? "activated" : "deactivated"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto space-y-4" data-testid="pv-status-master-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <ClipboardList className="w-6 h-6 text-yamaha-700" />
        </div>
        <div className="flex-1">
          <h1 className="text-[22px] font-semibold text-yamaha-800 leading-none tracking-tight">
            PV Status Master
          </h1>
          <p className="text-[12.5px] text-[#747684] mt-1">
            {(canAdd || canEdit)
              ? "Manage the statuses used across the Pending Vehicle module. Inactive statuses stay on existing records but no longer appear in future selections."
              : "View-only. Admin can add, edit, and activate/deactivate statuses."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="pv-status-refresh"
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2e1eb] text-[12px] text-[#444653] hover:bg-white"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {canAdd && (
            <button
              data-testid="pv-status-add"
              onClick={openAdd}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12px] font-semibold"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Status
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#747684]" />
        <input
          data-testid="pv-status-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search status or category…"
          className="w-full pl-8 pr-2 py-1.5 rounded-md border border-[#e2e1eb] text-[12.5px]"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="pv-status-table">
            <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Order</th>
                <th className="px-3 py-2 text-left">Status Name</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-center">EDD Mandatory</th>
                <th className="px-3 py-2 text-center">Remarks Mandatory</th>
                <th className="px-3 py-2 text-center">Ready-for-Delivery</th>
                <th className="px-3 py-2 text-center">Close Record</th>
                <th className="px-3 py-2 text-center">Active</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#eeecf6]">
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="px-3 py-10 text-center text-[12.5px] text-[#747684]">
                    No statuses found.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`pv-status-row-${r.id}`}
                  className={`hover:bg-[#faf8ff] ${!r.is_active ? "opacity-60" : ""}`}
                >
                  <td className="px-3 py-2 font-mono text-[12px] text-[#444653]">{r.display_order}</td>
                  <td className="px-3 py-2 font-semibold text-[#1a1b22]">{r.status_name}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${CAT_TONE[r.status_category] || "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]"}`}>
                      {CAT_LABEL[r.status_category]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">{r.edd_mandatory ? "Yes" : "—"}</td>
                  <td className="px-3 py-2 text-center">{r.remarks_mandatory ? "Yes" : "—"}</td>
                  <td className="px-3 py-2 text-center">{r.move_to_ready_for_delivery ? "Yes" : "—"}</td>
                  <td className="px-3 py-2 text-center">{r.close_record ? "Yes" : "—"}</td>
                  <td className="px-3 py-2 text-center">
                    {canEdit ? (
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!r.is_active}
                          onChange={() => toggleActive(r)}
                          data-testid={`pv-status-toggle-${r.id}`}
                        />
                        <span className={`text-[11px] font-semibold ${r.is_active ? "text-emerald-700" : "text-rose-600"}`}>
                          {r.is_active ? "Active" : "Inactive"}
                        </span>
                      </label>
                    ) : (
                      <span className={`text-[11px] font-semibold ${r.is_active ? "text-emerald-700" : "text-rose-600"}`}>
                        {r.is_active ? "Active" : "Inactive"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && (
                      <button
                        onClick={() => openEdit(r)}
                        data-testid={`pv-status-edit-${r.id}`}
                        className="inline-flex items-center gap-1 text-[11.5px] text-yamaha-700 font-semibold hover:underline"
                      >
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pv-status-form">
          <div className="bg-white rounded-xl border border-[#e2e1eb] w-full max-w-lg shadow-xl">
            <div className="px-5 py-3 border-b border-[#e2e1eb] flex items-center gap-2">
              <h3 className="text-[15px] font-semibold text-[#1a1b22] flex-1">
                {form.id ? "Edit Status" : "Add Status"}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-[#747684] hover:text-[#1a1b22]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
                  Status Name <span className="text-rose-600">*</span>
                </label>
                <input
                  data-testid="pv-status-form-name"
                  value={form.status_name}
                  onChange={(e) => setField("status_name", e.target.value)}
                  className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
                    Category <span className="text-rose-600">*</span>
                  </label>
                  <select
                    data-testid="pv-status-form-category"
                    value={form.status_category}
                    onChange={(e) => onCategoryChange(e.target.value)}
                    className="w-full border border-[#e2e1eb] rounded-md px-2 py-2 text-[13px]"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
                    Display Order <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    data-testid="pv-status-form-order"
                    value={form.display_order}
                    onChange={(e) => setField("display_order", e.target.value)}
                    className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                {[
                  ["edd_mandatory", "EDD Mandatory"],
                  ["remarks_mandatory", "Remarks Mandatory"],
                  ["move_to_ready_for_delivery", "Move to Ready for Delivery"],
                  ["close_record", "Close Record"],
                  ["is_active", "Active"],
                ].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-[12.5px]" data-testid={`pv-status-form-${k}`}>
                    <input
                      type="checkbox"
                      checked={!!form[k]}
                      onChange={(e) => setField(k, e.target.checked)}
                    />
                    {l}
                  </label>
                ))}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[#e2e1eb] flex justify-end gap-2">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-lg border border-[#e2e1eb] text-[12px] text-[#444653] hover:bg-[#faf8ff]"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={saving}
                data-testid="pv-status-form-save"
                className="px-4 py-2 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 disabled:opacity-50 text-white text-[12px] font-semibold"
              >
                {saving ? "Saving…" : form.id ? "Update" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PvStatusMaster;
